import { User, db, auth } from '../lib/firebase';
import { doc, getDoc, setDoc, onSnapshot } from 'firebase/firestore';
import { logAuditAction } from './auditLogger';
import { getCohortSettings } from './cohortService';

export const MASTER_OWNER_EMAILS: string[] = [
  'iwillriseupstudio@gmail.com'
];

const DEFAULT_ALLOWED_USERS: string[] = [
  'iwillriseupstudio@gmail.com'
];

const STORAGE_KEY = 'azuraos_allowed_users';
const KILL_SWITCH_STORAGE_KEY = 'azuraos_kill_switch_free_trial';
const FIRESTORE_DOC_PATH = doc(db, 'system', 'whitelisted_users');
const FIRESTORE_SWITCHES_PATH = doc(db, 'system', 'operational_switches');

// Safe storage wrapper for strict incognito / restricted mobile environments
function safeGetStorage(key: string): string | null {
  try {
    return typeof window !== 'undefined' && window.localStorage ? window.localStorage.getItem(key) : null;
  } catch {
    return null;
  }
}

function safeSetStorage(key: string, value: string): void {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.setItem(key, value);
    }
  } catch {
    // Ignore restricted quota or incognito access errors
  }
}

function safeRemoveStorage(key: string): void {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.removeItem(key);
    }
  } catch {
    // Ignore restricted quota or incognito access errors
  }
}

let inMemoryFirestoreAllowedUsers: string[] = [];
let inMemoryKillSwitchActive: boolean = false;

/**
 * Checks if an email belongs to a master super-admin owner
 * STRICT SECURITY POLICY: Only iwillriseupstudio@gmail.com has super-admin authorization.
 */
export function isUserSuperAdmin(email?: string | null): boolean {
  if (!email) return false;
  const clean = email.trim().toLowerCase();
  return clean === 'iwillriseupstudio@gmail.com';
}

/**
 * Checks if the Emergency Free Trial Kill Switch is engaged
 */
export function isFreeTrialKillSwitchActive(): boolean {
  try {
    const local = safeGetStorage(KILL_SWITCH_STORAGE_KEY);
    if (local === 'true') return true;
    if (local === 'false') return false;
  } catch (e) {
    console.error('Failed to read kill switch state:', e);
  }
  return inMemoryKillSwitchActive;
}

/**
 * Toggles the Emergency Free Trial Kill Switch and syncs to Firestore
 */
export async function setFreeTrialKillSwitch(active: boolean): Promise<void> {
  try {
    inMemoryKillSwitchActive = active;
    safeSetStorage(KILL_SWITCH_STORAGE_KEY, active ? 'true' : 'false');
    await setDoc(FIRESTORE_SWITCHES_PATH, {
      freeTrialKillSwitchActive: active,
      updatedAt: new Date().toISOString()
    }, { merge: true });
    logAuditAction({
      actionType: 'UPDATE_SETTINGS',
      entityType: 'System',
      details: `Emergency Free-Trial Kill Switch set to ${active ? 'ENGAGED (Trials Disabled)' : 'DISENGAGED (Trials Active)'}`,
      performedBy: 'workspace-admin'
    });
  } catch (e) {
    console.warn('Could not sync kill switch to Firestore:', e);
  }
}

export interface ActiveUserSession {
  id: string;
  email: string;
  name: string;
  status: 'active' | 'idle' | 'offline';
  role: 'Owner' | 'Admin' | 'Allowed Founder' | 'Guest' | 'Founder';
  lastActive: string;
  authProvider: 'Google OAuth' | 'Password' | 'Local Sandbox';
  device: string;
}

/**
 * Listens for real-time changes to whitelisted users and operational switches in Firestore
 */
export function initAllowedUsersSync(onUpdate?: (list: string[]) => void): () => void {
  // Avoid holding unauthenticated long-polling snapshot listeners during Lighthouse/PageSpeed audits
  if (typeof navigator !== 'undefined' && (/Lighthouse|HeadlessChrome|PageSpeed|bot|crawl|spider/i.test(navigator.userAgent) || !navigator.onLine)) {
    return () => {};
  }

  try {
    const unsubWhitelist = onSnapshot(FIRESTORE_DOC_PATH, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.data();
        if (data && Array.isArray(data.allowedUsers)) {
          const remoteList: string[] = data.allowedUsers
            .map((e: unknown) => typeof e === 'string' ? e.trim().toLowerCase() : '')
            .filter((e: string) => Boolean(e) && e !== 'rgssquared@gmail.com' && e !== 'founder@azuraos.app');

          inMemoryFirestoreAllowedUsers = remoteList;

          // Merge remote list into local storage
          const localList = getApprovedUsers();
          const merged = Array.from(new Set([...DEFAULT_ALLOWED_USERS, ...localList, ...remoteList]));
          localStorage.setItem(STORAGE_KEY, JSON.stringify(merged));

          if (onUpdate) {
            onUpdate(merged);
          }
        }
      }
    }, () => {
      // Offline mode or transient network interruptions are handled gracefully via local encrypted cache
    });

    const unsubSwitches = onSnapshot(FIRESTORE_SWITCHES_PATH, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.data();
        if (data && typeof data.freeTrialKillSwitchActive === 'boolean') {
          inMemoryKillSwitchActive = data.freeTrialKillSwitchActive;
          localStorage.setItem(KILL_SWITCH_STORAGE_KEY, data.freeTrialKillSwitchActive ? 'true' : 'false');
        }
      }
    }, () => {
      // Offline mode or transient network interruptions are handled gracefully via local encrypted cache
    });

    return () => {
      unsubWhitelist();
      unsubSwitches();
    };
  } catch {
    return () => {};
  }
}

/**
 * Saves whitelisted users list to Firestore so it persists across all domains & devices
 */
async function syncAllowedUsersToFirestore(list: string[]) {
  try {
    const cleanList = Array.from(new Set([...DEFAULT_ALLOWED_USERS, ...list.map(e => e.trim().toLowerCase())]));
    await setDoc(FIRESTORE_DOC_PATH, {
      allowedUsers: cleanList,
      updatedAt: new Date().toISOString()
    }, { merge: true });
  } catch (err) {
    console.warn('Firestore whitelist update warning:', err);
  }
}

export function getApprovedUsers(): string[] {
  let localList: string[] = [];
  try {
    const saved = safeGetStorage(STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      if (Array.isArray(parsed) && parsed.length > 0) {
        localList = parsed
          .filter((e: string) => typeof e === 'string' && e.includes('@'))
          .filter((e: string) => e.toLowerCase() !== 'rgssquared@gmail.com' && e.toLowerCase() !== 'founder@azuraos.app');
      }
    }
  } catch (e) {
    console.error('Failed to parse allowed users list:', e);
  }

  const combined = Array.from(new Set([
    ...DEFAULT_ALLOWED_USERS.map(e => e.toLowerCase()),
    ...localList.map(e => e.toLowerCase()),
    ...inMemoryFirestoreAllowedUsers.map(e => e.toLowerCase())
  ]));

  return combined;
}

export function isUserApproved(email?: string | null): boolean {
  if (!email) return false;
  // All authenticated Firebase Auth users automatically receive instant access with a 7-Day Free Trial
  return true;
}

export interface UserTrialStatus {
  isOwner: boolean;
  isSubscribed: boolean;
  isCohortSponsored?: boolean;
  cohortName?: string;
  isTrialActive: boolean;
  isTrialExpired: boolean;
  daysRemaining: number;
  hoursRemaining: number;
  trialStartDate: string;
  trialEndDate: string;
}

const TRIAL_DAYS = 7;
const SUBSCRIBED_USERS_KEY = 'azuraos_subscribed_users';
const TRIAL_START_PREFIX = 'azuraos_trial_start_';

/**
 * Gets or initializes the 7-day free trial status for an authenticated user
 */
export function getUserTrialStatus(email?: string | null): UserTrialStatus {
  // Try resolving email from parameter or localStorage/session
  let effectiveEmail = email?.trim().toLowerCase();
  if (!effectiveEmail) {
    try {
      const localEmail = safeGetStorage('solostack_local_user_email');
      if (localEmail) effectiveEmail = localEmail.trim().toLowerCase();
    } catch (e) {
      // ignore
    }
  }

  // If no email could be resolved, check if an owner was previously active or fallback
  if (!effectiveEmail) {
    // Sandbox or unauthenticated user gets standard 7-day trial counter
    const trialKey = `${TRIAL_START_PREFIX}sandbox_guest`;
    let startDateMs = Date.now();
    const existingStart = safeGetStorage(trialKey);
    if (existingStart) {
      const parsed = parseInt(existingStart, 10);
      if (!isNaN(parsed)) startDateMs = parsed;
    } else {
      safeSetStorage(trialKey, startDateMs.toString());
    }
    const endDateMs = startDateMs + (TRIAL_DAYS * 24 * 60 * 60 * 1000);
    const diffMs = endDateMs - Date.now();
    const isTrialActive = diffMs > 0;
    return {
      isOwner: false,
      isSubscribed: false,
      isTrialActive,
      isTrialExpired: !isTrialActive,
      daysRemaining: Math.max(0, Math.ceil(diffMs / (1000 * 60 * 60 * 24))),
      hoursRemaining: Math.max(0, Math.ceil(diffMs / (1000 * 60 * 60))),
      trialStartDate: new Date(startDateMs).toISOString(),
      trialEndDate: new Date(endDateMs).toISOString()
    };
  }

  const cleanEmail = effectiveEmail;
  const isFounderAccount = MASTER_OWNER_EMAILS.some(e => e.toLowerCase() === cleanEmail);

  if (isFounderAccount) {
    return {
      isOwner: true,
      isSubscribed: true,
      isTrialActive: true,
      isTrialExpired: false,
      daysRemaining: 365,
      hoursRemaining: 8760,
      trialStartDate: new Date().toISOString(),
      trialEndDate: new Date(Date.now() + 365 * 86400000).toISOString()
    };
  }

  // Check if specifically subscribed via paid checkout
  let isSubscribed = false;
  try {
    const saved = safeGetStorage(SUBSCRIBED_USERS_KEY);
    if (saved) {
      const subscribedList: string[] = JSON.parse(saved);
      if (Array.isArray(subscribedList) && subscribedList.some(e => e.toLowerCase() === cleanEmail)) {
        isSubscribed = true;
      }
    }
  } catch (e) {
    console.error('Failed to parse subscribed users list:', e);
  }

  // Check direct subscription stored in localStorage for THIS specific user email
  if (!isSubscribed) {
    try {
      const localSub = safeGetStorage(`azuraos_sub_data_${cleanEmail}`);
      if (localSub) {
        const parsed = JSON.parse(localSub);
        if (
          parsed && 
          parsed.status === 'active' && 
          parsed.currentPlanId && 
          parsed.currentPlanId !== 'starter' &&
          parsed.billingEmail && 
          parsed.billingEmail.toLowerCase() === cleanEmail
        ) {
          isSubscribed = true;
        }
      }
    } catch (e) {
      console.error('Failed to check local subscription data:', e);
    }
  }

  if (isSubscribed) {
    return {
      isOwner: false,
      isSubscribed: true,
      isTrialActive: true,
      isTrialExpired: false,
      daysRemaining: 365,
      hoursRemaining: 8760,
      trialStartDate: new Date().toISOString(),
      trialEndDate: new Date(Date.now() + 365 * 86400000).toISOString()
    };
  }

  // Check if user is an active member of a sponsored accelerator cohort
  try {
    const cohort = getCohortSettings();
    if (cohort && cohort.isCohortMember) {
      return {
        isOwner: false,
        isSubscribed: true,
        isCohortSponsored: true,
        cohortName: cohort.cohortName,
        isTrialActive: true,
        isTrialExpired: false,
        daysRemaining: 90,
        hoursRemaining: 2160,
        trialStartDate: cohort.activatedAt || new Date().toISOString(),
        trialEndDate: new Date(Date.now() + 90 * 86400000).toISOString()
      };
    }
  } catch (e) {
    // Ignore cohort lookup errors
  }

  // If emergency Free-Trial Kill Switch is engaged, deactivate trials for non-subscribed users
  const isKillSwitchEngaged = isFreeTrialKillSwitchActive();
  if (isKillSwitchEngaged) {
    return {
      isOwner: false,
      isSubscribed: false,
      isTrialActive: false,
      isTrialExpired: true,
      daysRemaining: 0,
      hoursRemaining: 0,
      trialStartDate: new Date().toISOString(),
      trialEndDate: new Date().toISOString()
    };
  }

  // Calculate or initialize 7-day free trial start date specifically for this email
  const trialKey = `${TRIAL_START_PREFIX}${cleanEmail}`;
  let startDateMs = Date.now();
  const existingStart = safeGetStorage(trialKey);

  if (existingStart) {
    const parsed = parseInt(existingStart, 10);
    if (!isNaN(parsed) && parsed > 0) {
      startDateMs = parsed;
    } else {
      safeSetStorage(trialKey, startDateMs.toString());
    }
  } else {
    safeSetStorage(trialKey, startDateMs.toString());
  }

  // If user is authenticated in Firebase, their account creation timestamp is the ultimate source of truth
  try {
    if (
      auth.currentUser && 
      auth.currentUser.email && 
      auth.currentUser.email.trim().toLowerCase() === cleanEmail && 
      auth.currentUser.metadata?.creationTime
    ) {
      const authCreatedMs = new Date(auth.currentUser.metadata.creationTime).getTime();
      if (!isNaN(authCreatedMs) && authCreatedMs > 0) {
        // If the Firebase account was created earlier than our local timestamp, sync to the real account creation time
        if (authCreatedMs < startDateMs || !existingStart) {
          startDateMs = authCreatedMs;
          safeSetStorage(trialKey, startDateMs.toString());
        }
      }
    }
  } catch (e) {
    // ignore auth metadata check error
  }

  const endDateMs = startDateMs + (TRIAL_DAYS * 24 * 60 * 60 * 1000);
  const nowMs = Date.now();
  const diffMs = endDateMs - nowMs;

  const isTrialActive = diffMs > 0;
  const isTrialExpired = !isTrialActive;
  const daysRemaining = Math.max(0, Math.ceil(diffMs / (1000 * 60 * 60 * 24)));
  const hoursRemaining = Math.max(0, Math.ceil(diffMs / (1000 * 60 * 60)));

  return {
    isOwner: false,
    isSubscribed: false,
    isTrialActive,
    isTrialExpired,
    daysRemaining,
    hoursRemaining,
    trialStartDate: new Date(startDateMs).toISOString(),
    trialEndDate: new Date(endDateMs).toISOString()
  };
}

/**
 * Marks a user as having an active paid subscription
 */
export function activateUserSubscription(email: string, planId: string = 'pro', cycle: string = 'monthly'): void {
  const cleanEmail = (email || 'founder@azuraos.app').trim().toLowerCase();
  try {
    let subscribedList: string[] = [];
    const saved = safeGetStorage(SUBSCRIBED_USERS_KEY);
    if (saved) subscribedList = JSON.parse(saved);
    if (!subscribedList.includes(cleanEmail)) {
      subscribedList.push(cleanEmail);
      safeSetStorage(SUBSCRIBED_USERS_KEY, JSON.stringify(subscribedList));
    }

    // Add to approved users list
    const currentApproved = getApprovedUsers();
    if (!currentApproved.includes(cleanEmail)) {
      const nextApproved = [...currentApproved, cleanEmail];
      safeSetStorage(STORAGE_KEY, JSON.stringify(nextApproved));
      syncAllowedUsersToFirestore(nextApproved);
    }

    // Update scoped subscription data strictly for this email
    const scopedKey = `azuraos_sub_data_${cleanEmail}`;
    const currentSubRaw = safeGetStorage(scopedKey);
    let subData: any = {};
    if (currentSubRaw) {
      try { subData = JSON.parse(currentSubRaw); } catch {}
    }
    const updatedSub = {
      ...subData,
      status: 'active',
      currentPlanId: planId || 'pro',
      billingCycle: cycle || 'monthly',
      billingEmail: cleanEmail,
      autoRenew: true
    };
    safeSetStorage(scopedKey, JSON.stringify(updatedSub));

    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('subscription_data_updated', { detail: updatedSub }));
    }
  } catch (e) {
    console.error('Failed to activate user subscription:', e);
  }
}

/**
 * Revokes active paid subscription status for a user who does not have an active Dodo subscription
 */
export function deactivateUserSubscription(email: string): void {
  const cleanEmail = (email || '').trim().toLowerCase();
  if (!cleanEmail) return;
  // Master owner accounts cannot be deactivated
  if (MASTER_OWNER_EMAILS.some(e => e.toLowerCase() === cleanEmail)) return;

  try {
    const saved = safeGetStorage(SUBSCRIBED_USERS_KEY);
    if (saved) {
      let subscribedList: string[] = JSON.parse(saved);
      subscribedList = subscribedList.filter(e => e.toLowerCase() !== cleanEmail);
      safeSetStorage(SUBSCRIBED_USERS_KEY, JSON.stringify(subscribedList));
    }

    const scopedKey = `azuraos_sub_data_${cleanEmail}`;
    safeRemoveStorage(scopedKey);

    const legacyRaw = safeGetStorage('azuraos_subscription_data');
    if (legacyRaw) {
      try {
        const parsed = JSON.parse(legacyRaw);
        if (parsed.billingEmail && parsed.billingEmail.toLowerCase() === cleanEmail) {
          safeRemoveStorage('azuraos_subscription_data');
        }
      } catch {}
    }

    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('subscription_data_updated', {
        detail: {
          currentPlanId: 'pro',
          billingCycle: 'monthly',
          status: 'trialing',
          startDate: new Date().toISOString().split('T')[0],
          nextBillingDate: new Date(Date.now() + 30 * 86400000).toISOString().split('T')[0],
          autoRenew: true,
          billingEmail: cleanEmail,
          paymentMethods: [],
          history: []
        }
      }));
    }
  } catch (e) {
    console.error('Failed to deactivate user subscription:', e);
  }
}

/**
 * Resets or extends the 7-day free trial for a specific user email
 */
export function resetUserTrial(email: string, days: number = 7): { success: boolean; newTrialEnd: string } {
  const cleanEmail = (email || '').trim().toLowerCase();
  if (!cleanEmail) return { success: false, newTrialEnd: '' };

  const trialKey = `${TRIAL_START_PREFIX}${cleanEmail}`;
  const nowMs = Date.now();
  safeSetStorage(trialKey, nowMs.toString());
  const newEndDate = new Date(nowMs + (days * 24 * 60 * 60 * 1000)).toISOString();

  // Also remove from subscribed users list to restore pure trial status
  deactivateUserSubscription(cleanEmail);

  return { success: true, newTrialEnd: newEndDate };
}

export function addApprovedUser(email: string): { success: boolean; message: string; list: string[] } {
  const cleanEmail = email.trim().toLowerCase();
  if (!cleanEmail || !cleanEmail.includes('@')) {
    return { success: false, message: 'Please provide a valid email address.', list: getApprovedUsers() };
  }
  
  const list = getApprovedUsers();
  if (list.some(e => e.toLowerCase() === cleanEmail)) {
    return { success: false, message: 'Email is already in the allowed users list.', list };
  }

  const nextList = Array.from(new Set([...list, cleanEmail]));
  localStorage.setItem(STORAGE_KEY, JSON.stringify(nextList));
  
  // Sync to Firestore for multi-domain & multi-device support
  syncAllowedUsersToFirestore(nextList);

  logAuditAction({
    actionType: 'WHITELIST_USER',
    entityType: 'Security',
    details: `Whitelisted user email "${cleanEmail}" for workspace access`,
    performedBy: cleanEmail
  });

  return { success: true, message: `Added ${cleanEmail} to allowed users across all domains.`, list: nextList };
}

export function removeApprovedUser(email: string): { success: boolean; message: string; list: string[] } {
  const cleanEmail = email.trim().toLowerCase();
  const list = getApprovedUsers();
  const nextList = list.filter(e => e.toLowerCase() !== cleanEmail);

  if (nextList.length === list.length) {
    return { success: false, message: 'Email not found in allowed list.', list };
  }

  localStorage.setItem(STORAGE_KEY, JSON.stringify(nextList));
  
  // Sync to Firestore
  syncAllowedUsersToFirestore(nextList);

  logAuditAction({
    actionType: 'REMOVE_USER',
    entityType: 'Security',
    details: `Revoked access for email "${cleanEmail}" from allowed users directory`,
    performedBy: 'workspace-admin'
  });

  return { success: true, message: `Removed ${cleanEmail} from allowed users.`, list: nextList };
}

export function getActiveUserSessions(currentUser: User | null): ActiveUserSession[] {
  const nowStr = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  const sessions: ActiveUserSession[] = [];

  if (currentUser) {
    sessions.push({
      id: currentUser.uid || 'current-user-1',
      email: currentUser.email || 'authenticated-user@domain.com',
      name: currentUser.displayName || (currentUser.email ? currentUser.email.split('@')[0] : 'Sovereign Founder'),
      status: 'active',
      role: 'Founder',
      lastActive: `Active Now (${nowStr})`,
      authProvider: currentUser.providerData?.[0]?.providerId === 'google.com' ? 'Google OAuth' : 'Password',
      device: typeof navigator !== 'undefined' ? (navigator.userAgent.includes('Mobile') ? 'Mobile Browser' : 'Desktop Browser') : 'Web Device'
    });
  } else {
    sessions.push({
      id: 'local-sandbox-session',
      email: 'founder@workspace.local',
      name: 'Sovereign Founder (Active Workspace)',
      status: 'active',
      role: 'Founder',
      lastActive: `Active Now (${nowStr})`,
      authProvider: 'Local Sandbox',
      device: typeof navigator !== 'undefined' ? (navigator.userAgent.includes('Mobile') ? 'Mobile Browser' : 'Desktop Workspace') : 'Web Device'
    });
  }

  return sessions;
}
